import { useRef } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { useDucatClient } from '@/context/DucatClientCtx';
import { canRegisterQuanta } from '@/lib/wallet-policy';

import { QueryKeys } from '../query-keys';

import type { QuantaHeaders } from './auth';
import { quantaApi } from './quantaApi';
import { quantaHeadersBelongToWallet } from './quantaIdentity';
import type { RegisterUser } from './useQuantaLogin';

export interface Stats {
  points: number;
  missions_completed: number;
  tasks_completed: number;
}

export interface Referral {
  referred: boolean;
  referrer: string | null;
  points: number;
}

export interface QuantaRegisterResponse {
  data: {
    user: RegisterUser;
    stats: Stats;
    referral: Referral;
    referral_reward?: number;
  };
}

interface QuantaRegisterParams {
  headers: QuantaHeaders;
  referralCode: string;
}

export const useQuantaRegister = (): UseMutationResult<QuantaRegisterResponse, AxiosError<{ error: string }>, QuantaRegisterParams> => {
  const { wallet, walletType, pubkey, accountRevision } = useDucatClient();
  const current = useRef({ wallet, walletType, pubkey, accountRevision });
  current.current = { wallet, walletType, pubkey, accountRevision };
  return useMutation<QuantaRegisterResponse, AxiosError<{ error: string }>, QuantaRegisterParams>({
    mutationKey: [QueryKeys.quantaRegister],
    mutationFn: async ({ headers, referralCode }: QuantaRegisterParams) => {
      const session = current.current;
      if (
        !session.wallet ||
        !canRegisterQuanta(session.walletType) ||
        !quantaHeadersBelongToWallet(headers, session.wallet, session.accountRevision, 'payment')
      ) {
        throw new Error('Connect MetaMask to register a new Quanta account.');
      }
      const path = referralCode ? `/registration?referral=${encodeURIComponent(referralCode)}` : '/registration';
      const response = await quantaApi.post(path, undefined, {
        headers: { ...headers, 'x-wallet-provider': 'metamask' },
      });

      return response.data;
    },
    // biome-ignore lint/suspicious/noExplicitAny: <TODO: fix any>
    onError: (e: any) => {
      if (e.status === 409 && e.response.data?.error === 'User with this wallet or public key already exists') {
        // This is OK.
      }
    },
  });
};
